import type { InfiniteData } from '@tanstack/react-query';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { useWmgSession } from '~/modules/auth/hooks/useWmgSession';

import { LabelQueryKeys } from '../config/queryKeys';
import { BFFClient } from '../lib/BFFClient';
import { getRequestHeaders } from '../lib/headers';
import { createPaginationQueryOptions } from '../lib/paginationOptions';
import {
  type LabelListResponse,
  LabelListResponseSchema,
} from '../lib/schemas';
import type { PresetUseInfiniteQueryOptions } from '../lib/types';
import type { PaginationVariables } from '../types/paginationVariables';
import type { SearchVariables } from '../types/searchVariables';

export interface LabelInfiniteListQueryVariables
  extends Omit<PaginationVariables, 'skip'>,
    SearchVariables {
  userId?: string;
  labelIds?: string;
  labelType?: string;
  parentId?: string;
  sortBy?: string;
  orderDir?: string;
  orderBy?: string;
  canCreateUsers?: boolean;
  canEditUsers?: boolean;
}

export function useLabelInfiniteList<TData = LabelListResponse>(
  variables: LabelInfiniteListQueryVariables,
  options?: PresetUseInfiniteQueryOptions<
    LabelListResponse,
    InfiniteData<TData>
  >,
) {
  const session = useWmgSession();
  const { take = 10, ...restVariables } = variables;

  const { data, ...query } = useInfiniteQuery({
    queryKey: LabelQueryKeys.infiniteList(variables),
    queryFn: async ({ pageParam }) => {
      const responseData = await BFFClient.get('labels', {
        headers: getRequestHeaders(session.token),
        searchParams: { ...restVariables, ...pageParam },
      }).json();

      return LabelListResponseSchema.parse(responseData);
    },
    placeholderData: keepPreviousData,
    ...createPaginationQueryOptions({ take }),
    ...options,
  });

  return { labelInfiniteList: data, ...query };
}
